import {
  memo,
  useCallback,
  useEffect,
  type ComponentType,
  type ReactElement,
} from 'react';
import { dispatchAction } from '@ardoq/rxbeach';
import { loadComponentPreview } from '@ardoq/component-overview/componentPreviews';
import {
  ComponentHoverPreview,
  type ComponentPreviewState,
  type HoverPreviewTriggerProps,
} from '@ardoq/component-overview/miniOverview';
import type { PopoverPlacement } from '@ardoq/popovers';
import {
  type ComponentItemProps,
  type HoverPreviewRowProps,
} from './PropTypes';

const LOADING_STATE: ComponentPreviewState = { status: 'loading' };

/**
 * `undefined` leaves the card under pointer control. Once any row holds the
 * keyboard highlight the others are forced shut, so a hovered row and a
 * highlighted row cannot both have a card open.
 */
export const toIsActive = ({
  isHighlighted,
  hasKeyboardHighlight,
  isDragging,
}: ComponentItemProps): boolean | undefined => {
  if (isDragging) return false;
  if (isHighlighted) return true;
  if (hasKeyboardHighlight) return false;
  return undefined;
};

// The row spans the full pane, so the default placement below the anchor would
// bury the rest of the list the user is picking from.
const PREVIEW_PLACEMENT: PopoverPlacement = {
  side: 'right',
  overflow: 'bottom',
  align: 'anchorEdge',
};

type PreviewAnchorRowProps = {
  Row: ComponentType<HoverPreviewRowProps>;
  componentItemProps: ComponentItemProps;
  triggerProps: HoverPreviewTriggerProps;
  onOpened: VoidFunction;
};

/**
 * `ComponentHoverPreview` fires `onOpen` on mouseenter rather than when the
 * card opens. Every row here is a full-width trigger, so loading from that
 * would fire a request per row a mouse sweep crosses; load when the card is
 * actually open instead.
 */
const PreviewAnchorRow = ({
  Row,
  componentItemProps,
  triggerProps,
  onOpened,
}: PreviewAnchorRowProps): ReactElement => {
  // `aria-haspopup` is deliberately not taken from triggerProps. It would
  // promise an interaction assistive tech cannot reach: the tree has no
  // per-row focus and no aria-activedescendant yet, so the highlighted row is
  // invisible to AT. `aria-details` on the treeitem describes the open card
  // without claiming it can be summoned.
  const {
    ref,
    onMouseEnter,
    onMouseLeave,
    'aria-expanded': isOpen,
    'aria-controls': popoverId,
  } = triggerProps;

  useEffect(() => {
    if (isOpen) onOpened();
  }, [isOpen, onOpened]);

  // `ComponentHoverPreview` acts on `isActive` when it changes, so a `false`
  // held for a whole drag cannot keep rows the pointer reaches later shut.
  const { isDragging } = componentItemProps;

  return (
    <Row
      {...componentItemProps}
      triggerProps={{
        ref,
        onMouseEnter: isDragging ? undefined : onMouseEnter,
        onMouseLeave,
      }}
      detailsId={isOpen ? popoverId : undefined}
    />
  );
};

/**
 * Wraps a navigator row renderer in a component preview card. The card opens
 * on hover, and on the keyboard highlight in panes that have one.
 *
 * Call this once at module level: the result is a distinct component type, so
 * building it per render would remount every row.
 *
 * Rendering previews requires the host app to register
 * `componentPreviewsRoutines`, and the pane to feed `componentPreviews$` and a
 * permission context into the navigator state. An empty permission context is
 * not a safe stand-in: it leaves `confidentialFieldPermissions` null, which
 * makes `canSeeConfidentialFieldByName` return true and renders confidential
 * fields unredacted.
 */
export const getHoverPreviewComponentItem = (
  Row: ComponentType<HoverPreviewRowProps>
) =>
  memo((props: ComponentItemProps): ReactElement => {
    const { nodeId, componentPreview, permissionContext, i18nLocale } = props;

    const handleOpened = useCallback(() => {
      dispatchAction(loadComponentPreview({ componentId: nodeId }));
    }, [nodeId]);

    if (permissionContext === null) {
      return <Row {...props} />;
    }

    return (
      <ComponentHoverPreview
        state={componentPreview ?? LOADING_STATE}
        isActive={toIsActive(props)}
        permissionContext={permissionContext}
        i18nLocale={i18nLocale}
        placement={PREVIEW_PLACEMENT}
        renderTrigger={triggerProps => (
          <PreviewAnchorRow
            Row={Row}
            componentItemProps={props}
            triggerProps={triggerProps}
            onOpened={handleOpened}
          />
        )}
      >
        {props.name}
      </ComponentHoverPreview>
    );
  });
